import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Modal, Spin } from 'antd';
import { connect } from 'dva';
import moment from 'moment';
import AdditionalInformation from './components/AdditionalInformation';
import CRMInformation from './components/CRMInformation';
import {
  ACTION_AI_OUTREACH_AGENTS_LOADLIST,
} from '../../../../models/aiOutreachAgents';
import OutreachInformation from './components/OutreachInformation';
import ContactPerson from './components/ContactPerson';

const getNextWorkingDay = () => {
  let date = moment();
  date.add(1, 'days');

  while (date.day() === 0 || date.day() === 6) {
    date = date.add(1, 'days');
  }

  return date;
};

function CreateHubspotTransferModal(props) {
  const isUsingAgent = props.app.defaultUiSettings.ai_outreach_agents;
  const isUsingAISummary = props.app.defaultUiSettings.llm_company_visit_context_completion;
  const minStep = isUsingAgent ? 1 : 3;
  const currentProject = props.app.currentProject;
  const [step, setStep] = useState(minStep);
  const [visible, setVisible] = useState(props.visible);
  const [formSummary, setFormSummary] = useState({
    peoples: [],
    due_date: getNextWorkingDay(),
    outreach_email_agent_id: null,
    hubspot_pipeline: currentProject.hubspot_pipeline_default_id,
    hubspot_dealstage: currentProject.hubspot_pipeline_default_stage_id,
  });

  const aiOutreachAgents = props.aiOutreachAgents.agentsFullList[currentProject.id] || [];
  const assignees = props.hubspotOwners.assigned[currentProject.id] || [];
  const pipelines = props.hubspot.pipelines[currentProject.id] || [];
  const dealTopics = props.hubspot.dealCategories[currentProject.id] || [];
  const dealClusters = props.hubspot.dealClusters[currentProject.id] || [];
  const dealTypes = props.hubspot.dealTypes[currentProject.id] || [];
  const countries = props.countries ? props.countries.countriesList : [];

  const goPrevStep = (data) => {
    if (step === minStep) {
      props.onCancel();
      return;
    }
    const values = { ...formSummary, ...data };
    setFormSummary(values);
    let newStep = step - 1;
    if (step === 3 && !values.peoples.length) {
      newStep -= 1;
    }
    setStep(newStep);
  };

  const saveStep = (data) => {
    const values = { ...formSummary, ...data };
    setFormSummary(values);
    if (step === 4) {
      saveForm(values);
      return;
    }
    let newStep = step + 1;
    if (step === 1 && !values.peoples.length) {
      newStep += 1;
    }
    setStep(newStep);
  };

  const saveForm = async (values) => {
    let hsCompany;
    if (!props.hubspotCompanyId) {
      hsCompany = await props.dispatch({
        type: 'hubspot/createHubspotCompany',
        payload: {
          projectId: currentProject.id,
          lxpCompanyId: props.lxpCompanyId,
          ownerId: values.assignee,
        },
      });

      if (!hsCompany) {
        return;
      }
    }

    const hubspotTransfer = {
      hubspot_user_id: values.assignee,
      hubspot_company_id: props.hubspotCompanyId || hsCompany.hs_object_id,
      due_date: values.due_date.format('YYYY-MM-DD'),
      subject: `${moment().format('DDMMYY')} - New lead ${props.companyName}`,
      notes: values.notes,
      company_id: props.lxpCompanyId,
      hubspot_pipeline: values.hubspot_pipeline,
      hubspot_dealstage: values.hubspot_dealstage,
      enrich_deal_description_with_ai_model: values.enrich_deal_description_with_ai_model || false,
      deal_category_custom_property: values.deal_category_custom_property,
      deal_cluster_custom_property: values.deal_cluster_custom_property,
      deal_type_custom_property: values.deal_type_custom_property,
      hubspot_transfer_priority: values.hubspot_transfer_priority,
      visit_id: props.visitId,
    };

    if (values.outreach_email_with_ai_model) {
      hubspotTransfer.outreach_email_with_ai_model = true;
      hubspotTransfer.outreach_email_agent_id = values.outreach_email_agent_id;
      hubspotTransfer.outreach_email_context_from_urls = (values.url_fields_value || []).filter(url => url);
      hubspotTransfer.outreach_email_context_persons_ids = values.peoples.map(person => person.id);
    } else {
      hubspotTransfer.outreach_email_with_ai_model = false;
      hubspotTransfer.outreach_email_agent_id = null;
      hubspotTransfer.outreach_email_context_from_urls = [];
      hubspotTransfer.outreach_email_context_persons_ids = [];
    }

    const task = await props.dispatch({
      type: 'hubspot/createHubspotTransfer',
      payload: {
        projectId: currentProject.id,
        hubspotTransfer,
      },
    });

    if (!task) {
      return;
    }

    props.onTaskCreated(task);
  };

  const stepComponents = {
    1: <ContactPerson
      data={formSummary}
      onSubmit={saveStep}
      goPrevStep={goPrevStep}
      countries={countries}
      companyName={props.companyName}
      companyDomain={props.companyDomain}
    />,
    2: <OutreachInformation
      data={formSummary}
      agentsList={aiOutreachAgents}
      onSubmit={saveStep}
      goPrevStep={goPrevStep}
    />,
    3: <AdditionalInformation
      data={formSummary}
      currentProject={currentProject}
      dealClusters={dealClusters}
      dealTopics={dealTopics}
      dealTypes={dealTypes}
      onSubmit={saveStep}
      goPrevStep={goPrevStep}
      isUsingAgent={isUsingAgent}
    />,
    4: <CRMInformation
      data={formSummary}
      currentProject={currentProject}
      pipelines={pipelines}
      assignees={assignees}
      onSubmit={saveStep}
      goPrevStep={goPrevStep}
      isUsingAgent={isUsingAgent}
      isUsingAISummary={isUsingAISummary}
    />,
  };

  useEffect(() => {
    props.dispatch({
      type: 'leadContact/clearState',
    });

    // owners only once
    if (assignees.length === 0) {
      props.dispatch({
        type: 'hubspotOwners/assigned',
        payload: {
          projectId: currentProject.id,
        },
      });
    }

    if (pipelines.length === 0) {
      props.dispatch({
        type: 'hubspot/loadPipelines',
        payload: {
          projectId: currentProject.id,
        },
      });
    }

    if (dealTopics.length === 0) {
      props.dispatch({
        type: 'hubspot/loadTransferProperties',
        payload: {
          projectId: currentProject.id,
        },
      });
    }

    if (aiOutreachAgents.length === 0 && isUsingAgent) {
      props.dispatch({
        type: ACTION_AI_OUTREACH_AGENTS_LOADLIST,
        payload: {
          projectId: currentProject.id,
        },
      });
    }

    if (countries.length === 0 && isUsingAgent) {
      props.dispatch({ type: 'countries/loadList' });
    }

    return () => {
      props.dispatch({
        type: 'leadContact/clearState',
      });
    };
  }, []);

  const closeModal = () => {
    const modal = Modal.confirm({
      title: props.intl.formatMessage({ id: 'Common.Attention' }),
      content: props.intl.formatMessage({ id: 'Common.DiscardChanges' }),
      okText: props.intl.formatMessage({ id: 'Common.Agree' }),
      onOk: () => {
        hide();
        modal.destroy();
      },
      cancelText: props.intl.formatMessage({ id: 'Common.Cancel' }),
      width: 500,
    });
  };

  const hide = async () => {
    setVisible(false);
    setStep(3);
    await props.onCancel();
  };

  return (
    <Modal
      title={props.intl.formatMessage({ id: `Component.CreateHubspotTask.Form.Header.${step}` })}
      visible={visible}
      onCancel={closeModal}
      footer={null}
      destroyOnClose
      width={720}
    >
      <Spin
        spinning={
          !!props.loading.effects['hubspotOwners/assigned'] ||
        !!props.loading.effects['hubspot/createHubspotTransfer'] ||
        !!props.loading.effects['hubspot/createHubspotCompany'] ||
        !!props.loading.effects['hubspot/loadPipelines'] ||
        !!props.loading.effects['hubspot/loadTransferProperties']}
      >
        {stepComponents[step]}
      </Spin>
    </Modal>
  );
}

CreateHubspotTransferModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  hubspotCompanyId: PropTypes.string,
  visitId: PropTypes.number,
  lxpCompanyId: PropTypes.number,
  companyName: PropTypes.string,
  companyDomain: PropTypes.string,
  onTaskCreated: PropTypes.func,
};

export default connect((
  {
    app, loading, hubspot, hubspotOwners, aiOutreachAgents, countries,
  },
) => (
  {
    app, loading, hubspot, hubspotOwners, aiOutreachAgents, countries,
  }
))(injectIntl(CreateHubspotTransferModal));



// WEBPACK FOOTER //
// ./src/components/Leads/Hubspot/CreateHubspotTransferModal/index.js